// @ts-nocheck
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import type {
  CartCheckoutError,
  CartCheckoutRequest,
  CartCheckoutResponse,
  CartQuote,
  CartQuoteRequest,
  CheckoutOrderStatus,
  StoryUnlockError,
  StoryUnlockResponse,
} from "../../models";
import { customInstance } from "../../custom-instance";

type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

/**
 * Re-prices the cart, creates a CheckoutOrder and a Stripe Checkout Session, and returns the Stripe URL to redirect to. Returns 400 with the priced quote when the cart is invalid.
 * @summary Start Stripe checkout for cart lines
 */
export const storefrontCartCheckout = (
  cartCheckoutRequest: CartCheckoutRequest,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<CartCheckoutResponse>(
    {
      url: `/api/storefront/cart/checkout/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: cartCheckoutRequest,
      signal,
    },
    options
  );
};

export const getStorefrontCartCheckoutMutationOptions = <
  TError = CartCheckoutError,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storefrontCartCheckout>>,
    TError,
    { data: CartCheckoutRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof storefrontCartCheckout>>,
  TError,
  { data: CartCheckoutRequest },
  TContext
> => {
  const mutationKey = ["storefrontCartCheckout"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof storefrontCartCheckout>>,
    { data: CartCheckoutRequest }
  > = (props) => {
    const { data } = props ?? {};

    return storefrontCartCheckout(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StorefrontCartCheckoutMutationResult = NonNullable<
  Awaited<ReturnType<typeof storefrontCartCheckout>>
>;
export type StorefrontCartCheckoutMutationBody = CartCheckoutRequest;
export type StorefrontCartCheckoutMutationError = CartCheckoutError;

/**
 * @summary Start Stripe checkout for cart lines
 */
export const useStorefrontCartCheckout = <
  TError = CartCheckoutError,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storefrontCartCheckout>>,
    TError,
    { data: CartCheckoutRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof storefrontCartCheckout>>,
  TError,
  { data: CartCheckoutRequest },
  TContext
> => {
  const mutationOptions = getStorefrontCartCheckoutMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Prices the browser cart from the backend catalog. Anonymous callers can quote products; lines tied to a story require login. Lines that can't be bought carry an `error` and are excluded from totals.
 * @summary Price and validate cart lines
 */
export const storefrontCartQuote = (
  cartQuoteRequest: CartQuoteRequest,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<CartQuote>(
    {
      url: `/api/storefront/cart/quote/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: cartQuoteRequest,
      signal,
    },
    options
  );
};

export const getStorefrontCartQuoteMutationOptions = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storefrontCartQuote>>,
    TError,
    { data: CartQuoteRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof storefrontCartQuote>>,
  TError,
  { data: CartQuoteRequest },
  TContext
> => {
  const mutationKey = ["storefrontCartQuote"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof storefrontCartQuote>>,
    { data: CartQuoteRequest }
  > = (props) => {
    const { data } = props ?? {};

    return storefrontCartQuote(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StorefrontCartQuoteMutationResult = NonNullable<
  Awaited<ReturnType<typeof storefrontCartQuote>>
>;
export type StorefrontCartQuoteMutationBody = CartQuoteRequest;
export type StorefrontCartQuoteMutationError = unknown;

/**
 * @summary Price and validate cart lines
 */
export const useStorefrontCartQuote = <
  TError = unknown,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storefrontCartQuote>>,
    TError,
    { data: CartQuoteRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof storefrontCartQuote>>,
  TError,
  { data: CartQuoteRequest },
  TContext
> => {
  const mutationOptions = getStorefrontCartQuoteMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Status of the caller's checkout order for a Stripe Checkout Session id. A PENDING order is synced from Stripe (and fulfilled if paid), so the success page works even when the webhook is late. Poll until the status is FULFILLED, FAILED or EXPIRED.
 * @summary Checkout order status
 */
export const storefrontCheckoutOrderStatus = (
  sessionId: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<CheckoutOrderStatus>(
    {
      url: `/api/storefront/checkout/orders/${sessionId}/`,
      method: "GET",
      signal,
    },
    options
  );
};

export const getStorefrontCheckoutOrderStatusQueryKey = (sessionId: string) => {
  return [`/api/storefront/checkout/orders/${sessionId}/`] as const;
};

export const getStorefrontCheckoutOrderStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof storefrontCheckoutOrderStatus>>,
  TError = void
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storefrontCheckoutOrderStatus>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getStorefrontCheckoutOrderStatusQueryKey(sessionId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof storefrontCheckoutOrderStatus>>
  > = ({ signal }) =>
    storefrontCheckoutOrderStatus(sessionId, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!sessionId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof storefrontCheckoutOrderStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type StorefrontCheckoutOrderStatusQueryResult = NonNullable<
  Awaited<ReturnType<typeof storefrontCheckoutOrderStatus>>
>;
export type StorefrontCheckoutOrderStatusQueryError = void;

export function useStorefrontCheckoutOrderStatus<
  TData = Awaited<ReturnType<typeof storefrontCheckoutOrderStatus>>,
  TError = void
>(
  sessionId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storefrontCheckoutOrderStatus>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof storefrontCheckoutOrderStatus>>,
          TError,
          Awaited<ReturnType<typeof storefrontCheckoutOrderStatus>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useStorefrontCheckoutOrderStatus<
  TData = Awaited<ReturnType<typeof storefrontCheckoutOrderStatus>>,
  TError = void
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storefrontCheckoutOrderStatus>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof storefrontCheckoutOrderStatus>>,
          TError,
          Awaited<ReturnType<typeof storefrontCheckoutOrderStatus>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useStorefrontCheckoutOrderStatus<
  TData = Awaited<ReturnType<typeof storefrontCheckoutOrderStatus>>,
  TError = void
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storefrontCheckoutOrderStatus>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Checkout order status
 */

export function useStorefrontCheckoutOrderStatus<
  TData = Awaited<ReturnType<typeof storefrontCheckoutOrderStatus>>,
  TError = void
>(
  sessionId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof storefrontCheckoutOrderStatus>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getStorefrontCheckoutOrderStatusQueryOptions(
    sessionId,
    options
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Turns the caller's preview story into a full story and starts generating the remaining pages. Deducts one STORY_GENERATION immediately (the generation task won't charge again). Returns 402 when the user doesn't have enough credits and 409 while the preview is still being generated. Idempotent for already-unlocked stories.
 * @summary Unlock a preview story with credits
 */
export const storefrontStoryUnlock = (
  storyId: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<StoryUnlockResponse>(
    {
      url: `/api/storefront/stories/${storyId}/unlock/`,
      method: "POST",
      signal,
    },
    options
  );
};

export const getStorefrontStoryUnlockMutationOptions = <
  TError = StoryUnlockError,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storefrontStoryUnlock>>,
    TError,
    { storyId: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof storefrontStoryUnlock>>,
  TError,
  { storyId: number },
  TContext
> => {
  const mutationKey = ["storefrontStoryUnlock"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof storefrontStoryUnlock>>,
    { storyId: number }
  > = (props) => {
    const { storyId } = props ?? {};

    return storefrontStoryUnlock(storyId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StorefrontStoryUnlockMutationResult = NonNullable<
  Awaited<ReturnType<typeof storefrontStoryUnlock>>
>;

export type StorefrontStoryUnlockMutationError = StoryUnlockError;

/**
 * @summary Unlock a preview story with credits
 */
export const useStorefrontStoryUnlock = <
  TError = StoryUnlockError,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof storefrontStoryUnlock>>,
    TError,
    { storyId: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof storefrontStoryUnlock>>,
  TError,
  { storyId: number },
  TContext
> => {
  const mutationOptions = getStorefrontStoryUnlockMutationOptions(options);

  return useMutation(mutationOptions);
};
